import { FC } from "react";
import { useTranslation } from "next-i18next";
import Image from "next/image";
import Link from "next/link";
import { useAppContext } from "@/src/config/AppProvider";
import useBrowserRoutes from "@/src/utils/helpers/useBrowserRoutes";
import useSocialMediaIcons from "@/src/utils/helpers/useSocialMediaIcons";

interface IFooterOneProps {
  bgColor?: string;
}

const Footer: FC<IFooterOneProps> = ({ bgColor }) => {
  const { licenceSettings, programs, getLocalization } = useAppContext();
  const routes = useBrowserRoutes();
  const { t } = useTranslation();
  const { getSocialMediaIcon } = useSocialMediaIcons();

  return (
    <footer className={`rbt-footer footer-style-1 ${bgColor ? bgColor : "bg-color-white"} overflow-hidden`}>
      <div className="footer-top">
        <div className="container">
          <div className="row g-5">
            <div className="col-lg-3 col-md-6 col-sm-6 col-12">
              <div className="footer-widget">
                <div className="logo">
                  <Link href="/">
                    <Image src={licenceSettings.LogoUrl} width={152} height={50} alt={licenceSettings.SiteTitle} />
                  </Link>
                </div>
                <p className="description mt--20">{getLocalization("footerSlogan")}</p>
                <ul className="social-icon social-default justify-content-start">
                  {licenceSettings.SocialMediaLinks.map(social => (
                    <li key={social.IdGuid}>
                      <Link href={social.PlatformUrl} target="_blank">
                        {getSocialMediaIcon(social.PlatformName)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <FooterLinks
              classOne="col-lg-2 col-md-6 col-sm-6 col-12 mt--30"
              title={t("corporate")}
              links={[
                {
                  text: t("aboutUs"),
                  link: routes.aboutUs
                },
                {
                  text: t("ourTeam"),
                  link: routes.team
                },
                {
                  text: t("articles"),
                  link: routes.articles
                },
                {
                  text: t("announcements"),
                  link: routes.announcements
                }
              ]}
            />
            <FooterLinks
              classOne="col-lg-4 col-md-6 col-sm-6 col-12"
              title={t("programs")}
              links={programs?.slice(0, 6).map(item => {
                const detailLink = routes.programDetail.replace(":slug", item.Slug);
                return {
                  text: item.Title,
                  link: detailLink
                };
              })}
            />
            <div className="col-lg-3 col-md-6 col-sm-6 col-12">
              <div className="footer-widget">
                <h5 className="ft-title">{t("contactInformation")}</h5>
                <ul className="ft-link">
                  <li>
                    <i className="feather-phone mr--10"></i>
                    {licenceSettings.PhoneNumber}
                  </li>
                  <li>
                    <i className="feather-mail mr--10"></i>
                    <Link href={`mailto:${licenceSettings.ContactEmail}`}>{licenceSettings.ContactEmail}</Link>
                  </li>
                  <li>
                    <i className="feather-map-pin mr--10"></i>
                    {licenceSettings.Address}
                  </li>
                </ul>
                <div className="contact-btn mt--30">
                  <Link className="rbt-btn hover-icon-reverse btn-border-gradient radius-round" href={routes.contact}>
                    <div className="icon-reverse-wrapper">
                      <span className="btn-text">{t("contactWithUs")}</span>
                      <span className="btn-icon">
                        <i className="feather-arrow-right"></i>
                      </span>
                      <span className="btn-icon">
                        <i className="feather-arrow-right"></i>
                      </span>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

interface IFooterLinksProps {
  classOne: string;
  title: string;
  links: { link: string; text: string }[];
}

const FooterLinks: FC<IFooterLinksProps> = ({ classOne, title, links }) => {
  return (
    <div className={classOne}>
      <div className="footer-widget">
        <h5 className="ft-title">{title}</h5>
        <ul className="ft-link">
          {links?.map((item, index) => (
            <li key={index}>
              <Link href={item.link}>{item.text}</Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Footer;
